import { Action, createReducer, on } from '@ngrx/store';
import { ListUserVisualDto } from 'src/app/services/api.services';
import { VisualsActions } from '.';
import { VisualChartType, getInsightsPbiChartType } from '../../../shared/chart/chart';

export enum VisualsCategory {
  User = 'user',
  Shared = 'shared',
  Library = 'library'
}

export enum VisualsDisplayMode {
  Grid = 'grid',
  List = 'list'
}

export interface VisualsSortMode {
  column: string;
  descending: boolean;
}

export interface VisualsDisplayParams {
  category?: VisualsCategory;
  favouriteOnly?: boolean;
  displayMode?: VisualsDisplayMode;
  sortMode?: VisualsSortMode;
}

export const VisualsDefaultDisplayParams: VisualsDisplayParams = {
  category: VisualsCategory.User,
  favouriteOnly: false,
  displayMode: VisualsDisplayMode.List,
  sortMode: {
    column: 'updated',
    descending: true
  }
};

export const VisualsSortColumns = [
  'name', 'updatedBy', 'updated', 'created'
];

export type VisualListItem = ListUserVisualDto & {
  chartType: VisualChartType;
  visible?: boolean;
  selected?: boolean;
  locked?: boolean;
  organisationMismatch?: boolean;
};

export interface State {
  loadingVisuals: boolean;
  copyingVisual: boolean;
  deletingVisual: boolean;
  togglingVisualLibrary: boolean;
  sharingVisual: boolean;
  togglingVisualIsSample: boolean;
  deletingSampleForUser: boolean;

  expiringCache: boolean;

  visuals: VisualListItem[];

  selectedVisualIds: number[];

  displayParams: VisualsDisplayParams;
}

export const initialState: State = {
  loadingVisuals: false,
  copyingVisual: false,
  deletingVisual: false,
  sharingVisual: false,
  togglingVisualLibrary: false,
  togglingVisualIsSample: false,
  deletingSampleForUser: false,

  expiringCache: false,

  visuals: [],

  selectedVisualIds: [],

  displayParams: { ...VisualsDefaultDisplayParams }
};

const visualsReducer = createReducer(
  initialState,

  on(VisualsActions.getVisuals, (state) => {
    return {
      ...state,
      loadingVisuals: true
    };
  }),
  on(VisualsActions.getVisualsSuccess, (state, { visuals }) => {
    return {
      ...state,
      visuals: visuals.map(v => ({
        ...v,
        chartType: getInsightsPbiChartType(v.type),
        locked: false
      })),
      selectedVisualIds: getValidSelectedVisualIds(state),
      loadingVisuals: false
    };
  }),
  on(VisualsActions.getVisualsFailure, (state) => {
    return {
      ...state,
      loadingVisuals: false,
    };
  }),


  on(VisualsActions.expireCache, (state, { visualIds }) => {
    return {
      ...state,
      expiringCache: true
    };
  }),
  on(VisualsActions.expireCacheCompleted, (state, { visualIds }) => {
    const ids = new Set<number>(visualIds);

    return {
      ...state,
      visuals: state.visuals.map(v => ids.has(v.id) ? { ...v, userDatasetExpired: true } : v),
      expiringCache: false
    };
  }),


  on(VisualsActions.copyVisuals, (state) => {
    return {
      ...state,
      copyingVisual: true
    };
  }),
  on(VisualsActions.copyVisualsSuccess, (state, { visuals }) => {
    const items = visuals.map(v => <VisualListItem>{ ...v, chartType: getInsightsPbiChartType(v.type) });

    return {
      ...state,
      visuals: [...state.visuals, ...items],
      copyingVisual: false
    };
  }),
  on(VisualsActions.copyVisualsFailure, (state) => {
    return {
      ...state,
      copyingVisual: false,
    };
  }),


  on(VisualsActions.deleteVisuals, (state) => {
    return {
      ...state,
      deletingVisual: true
    };
  }),
  on(VisualsActions.deleteVisualsSuccess, (state, { visualIds }) => {
    let remaining = visualIds.length;

    return {
      ...state,
      visuals: state.visuals.filter(v => {
        if (remaining == 0)
          return true;

        let removed = visualIds.includes(v.id);
        if (removed) {
          remaining--;
          return false;
        }

        return true;
      }),
      selectedVisualIds: getValidSelectedVisualIds(state),
      deletingVisual: false
    };
  }),
  on(VisualsActions.deleteVisualsFailure, (state) => {
    return {
      ...state,
      deletingVisual: false,
    };
  }),


  on(VisualsActions.shareVisual, (state) => {
    return {
      ...state,
      sharingVisual: true
    };
  }),
  on(VisualsActions.shareVisualSuccess, (state) => {
    return {
      ...state,
      sharingVisual: false
    };
  }),
  on(VisualsActions.shareVisualFailure, (state) => {
    return {
      ...state,
      sharingVisual: false,
    };
  }),


  on(VisualsActions.updateUserVisual, (state) => {
    return {
      ...state
    };
  }),
  on(VisualsActions.updateUserVisualSuccess, (state, { userVisual }) => {
    const visualIndex = state.visuals.findIndex(d => d.id === userVisual.visualId);

    if (visualIndex === -1)
      return state;

    const visuals = [...state.visuals];

    const visual = visuals[visualIndex];

    visuals[visualIndex] = {
      ...visual,
      favourite: userVisual.favourite,
      home: userVisual.home
    };

    return {
      ...state,
      visuals
    };
  }),
  on(VisualsActions.updateUserVisualFailure, (state) => {
    return {
      ...state,
    };
  }),

  on(VisualsActions.toggleVisualLibrary, (state) => {
    return {
      ...state,
      togglingVisualLibrary: true
    };
  }),
  on(VisualsActions.toggleVisualLibrarySuccess, (state, { visualId, fromLibrary }) => {
    const visuals = [...state.visuals];

    const visualIndex = visuals.findIndex(d => d.id === visualId);

    visuals[visualIndex] = {
      ...visuals[visualIndex],
      fromLibrary
    };

    return {
      ...state,
      visuals,
      togglingVisualLibrary: false
    };
  }),
  on(VisualsActions.toggleVisualLibraryFailure, (state) => {
    return {
      ...state,
      togglingVisualLibrary: false,
    };
  }),


  on(VisualsActions.setSelectedVisualIds, (state, { selectedVisualIds }) => {
    return {
      ...state,
      selectedVisualIds
    };
  }),

  on(VisualsActions.updateDisplayParams, (state, { displayParams }) => {
    return {
      ...state,
      displayParams: {
        ...state.displayParams,
        ...displayParams
      }
    };
  }),


  on(VisualsActions.toggleVisualIsSample, (state) => {
    return {
      ...state,
      togglingVisualIsSample: true
    };
  }),
  on(VisualsActions.toggleVisualIsSampleSuccess, (state, { visualId, sample }) => {
    const visuals = [...state.visuals];

    const visualIndex = visuals.findIndex(d => d.id === visualId);

    visuals[visualIndex] = {
      ...visuals[visualIndex],
      sample
    };

    return {
      ...state,
      visuals,
      togglingVisualIsSample: false
    };
  }),
  on(VisualsActions.toggleVisualIsSampleFailure, (state) => {
    return {
      ...state,
      togglingVisualIsSample: false,
    };
  }),


  on(VisualsActions.deleteSampleForUser, (state) => {
    return {
      ...state,
      deletingSampleForUser: true
    };
  }),
  on(VisualsActions.deleteSampleForUserSuccess, (state) => {
    return {
      ...state,
      deletingSampleForUser: false
    };
  }),
  on(VisualsActions.deleteSampleForUserFailure, (state) => {
    return {
      ...state,
      deletingSampleForUser: false
    };
  }),

);

export function getValidSelectedVisualIds(state: State): number[] {
  if (state.visuals?.length) {
    const visualIds = new Set<number>(state.visuals.map(v => v.id));
    const validIds = state.selectedVisualIds?.filter(id => visualIds.has(id)) ?? [];
    if (validIds.length !== state.selectedVisualIds?.length) {
      return validIds;
    }
  }

  return state.selectedVisualIds ?? [];
}

export function reducer(state: State | undefined, action: Action) {
  return visualsReducer(state, action);
}
